import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import { FiGlobe, FiUsers, FiMapPin, FiCalendar } from 'react-icons/fi';
import { Lightbulb } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import { generateStructuredData } from '../lib/seoStructuredData';

export default function Home() {
  const { t } = useTranslation();
  const [hideFeature, setHideFeature] = useState(false);

  // Hide the sticky feature row when the footer becomes visible
  useEffect(() => {
    const footerEl = document.querySelector('footer');
    if (!footerEl) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const isFooterVisible = entries?.[0]?.isIntersecting;
        setHideFeature(!!isFooterVisible);
      },
      { root: null, threshold: 0.01 }
    );
    observer.observe(footerEl);
    return () => observer.disconnect();
  }, []);
  return (
    <>
      <SEO
        title="Kreatives - Nordic Creative Talent Platform | Find Freelancers & Creative Services"
        description="Connect with talented Nordic creatives across Sweden, Norway, Denmark, Finland, and Iceland. Find freelancers, designers, developers, and creative professionals for your projects."
        keywords="nordic creatives, freelancers, designers, developers, creative services, sweden, norway, denmark, finland, iceland, talent platform, creative professionals"
        image="/logo-kreativ-light.png"
        type="website"
        structuredData={generateStructuredData.homepage()}
        alternateLanguages={[
          { lang: 'en', href: `${window.location.origin}/` },
          { lang: 'sv', href: `${window.location.origin}/sv/` },
          { lang: 'no', href: `${window.location.origin}/no/` },
          { lang: 'da', href: `${window.location.origin}/da/` },
          { lang: 'fi', href: `${window.location.origin}/fi/` },
          { lang: 'is', href: `${window.location.origin}/is/` }
        ]}
      />
      <div className="flex flex-col min-h-[calc(100vh-1.5rem)] gap-10">
      {/* Wordmark / Logo centered like screenshot */}
      <section className="text-center space-y-6 pt-8">
        <div className="flex items-center justify-center">
          <Logo className="h-36 md:h-48 w-auto" alt="Kreatives" variant="footer" />
        </div>
        {/* Buttons: stack vertically on mobile, horizontal on desktop */}
        <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-4 mt-12 mb-4 px-4">
          {/* Register now CTA with glow/shadow */}
          <Link
            to="/how-it-works"
            className="cta-brand py-3 md:py-4 text-base md:text-lg w-[85%] max-w-[300px] md:w-[300px]"
            data-variant="primary"
          >
            {t('actions.register')}
          </Link>
          {/* View talents map CTA with glow/shadow */}
          <Link
            to="/creatives/map"
            className="cta-brand py-3 md:py-4 text-base md:text-lg flex items-center justify-center gap-2 whitespace-nowrap w-[85%] max-w-[300px] md:w-[300px]"
            data-variant="primary"
          >
            {t('actions.viewTalentsMap', { defaultValue: 'View Creatives Map' })}
            <FiMapPin className="w-4 h-4 md:w-5 md:h-5" />
          </Link>
        </div>
      </section>

      {/* Feature cards row as placeholder (Countries/Services/Talents) */}
      <section className={`container mt-auto sticky bottom-0 mb-1 transition-opacity ease-in-out duration-700 md:duration-1000 ${hideFeature ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        <div className="feature-row w-full mx-auto rounded-t-2xl border-2 border-b-0 border-black dark:border-white pt-3 pb-1 md:pt-4 md:pb-1 min-h-[14vh] md:min-h-[15vh]">
          <div className="grid grid-cols-4 gap-2 md:gap-12 text-center h-full items-end px-2 md:px-4">
            <Link
              to="/countries"
              aria-label="Browse Countries"
              className="nav-link group inline-flex flex-col items-center justify-center space-y-2 md:space-y-3 rounded-xl px-1 md:px-6 pt-3 pb-2 md:pt-5 md:pb-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1756fd] focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-zinc-900 transition-colors"
            >
              <FiGlobe className="w-6 h-6 md:w-8 md:h-8 text-current transition-colors" />
              <div className="text-xs md:text-base font-semibold text-current">{t('nav.countries')}</div>
            </Link>
            <Link
              to="/services"
              aria-label="Browse Services"
              className="nav-link group inline-flex flex-col items-center justify-center space-y-2 md:space-y-3 rounded-xl px-1 md:px-6 pt-3 pb-2 md:pt-5 md:pb-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1756fd] focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-zinc-900 transition-colors"
            >
              <Lightbulb className="w-6 h-6 md:w-8 md:h-8 text-current transition-colors" />
              <div className="text-xs md:text-base font-semibold text-current">{t('nav.services')}</div>
            </Link>
            <Link
              to="/creatives"
              aria-label="Browse Creatives"
              className="nav-link group inline-flex flex-col items-center justify-center space-y-2 md:space-y-3 rounded-xl px-1 md:px-6 pt-3 pb-2 md:pt-5 md:pb-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1756fd] focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-zinc-900 transition-colors"
            >
              <FiUsers className="w-6 h-6 md:w-8 md:h-8 text-current transition-colors" />
              <div className="text-xs md:text-base font-semibold text-current">{t('nav.talents', { defaultValue: 'Creatives' })}</div>
            </Link>
            <Link
              to="/events"
              aria-label="Browse Events"
              className="nav-link group inline-flex flex-col items-center justify-center space-y-2 md:space-y-3 rounded-xl px-1 md:px-6 pt-3 pb-2 md:pt-5 md:pb-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1756fd] focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-zinc-900 transition-colors"
            >
              <FiCalendar className="w-6 h-6 md:w-8 md:h-8 text-current transition-colors" />
              <div className="text-xs md:text-base font-semibold text-current">Events</div>
            </Link>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
